import { IGetChargingPointResponse } from 'models/http/chargingPoint';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OnChangeValue } from 'react-select';
import { ConnectorPowerLabel, ConnectorPowerType, ConnectorStatus, ConnectorType, ConnectorTypeLabel } from 'utils/enums/chargingType';
import { useHttp } from 'utils/hooks';
import useDebounceState from 'utils/hooks/use-debounce-state';
import { MSelectChangeData, MSelectOption } from 'views/MSelect/MSelect.model';

const MapLayersHook = (
  search?: string,
): {
  loadingState: boolean;
  connectorFilter?: ConnectorStatus | null;
  powerTypeFilters: {
    id: ConnectorPowerLabel;
    label: string;
  }[];
  connectorTypeFilters: {
    id: ConnectorTypeLabel;
    label: string;
  }[];
  operatorFilters: {
    id: string;
    label: string;
  }[];
  updatePowerTypeFilter: (value: OnChangeValue<MSelectChangeData<null, ConnectorPowerLabel>, true>) => void;
  updateOpertorFilter: (value: OnChangeValue<MSelectChangeData<null, string>, true>) => void;
  updateConnectorStatusFilter: (connectorFilterStatus: ConnectorStatus) => void;
  updateConnectorTypeFilter: (value: OnChangeValue<MSelectChangeData<null, ConnectorTypeLabel>, true>) => void;
  updateVehicleConnectorTypeFilter: (connectorTypes: ConnectorType[]) => void;
  chargingPoints?: IGetChargingPointResponse['records'];
} => {
  const http = useHttp();

  const { t } = useTranslation();

  const [loadingState, setLoadingState] = useState<boolean>(false);

  const [chargingPointsState, setChargingPointsState] = useState<IGetChargingPointResponse['records']>();
  const [connectorFilterStatus, setConnectorFilterStatus] = useState<ConnectorStatus | null>(null);

  const [operatorFilterState, debouncedOperatorFilterState, setoOperatorFilterState] = useDebounceState<string[]>([], 250);

  const [powerTypeFiltersState, debouncedPowerTypeFiltersState, setPowerTypeFiltersState] = useDebounceState<ConnectorPowerLabel[]>(
    [],
    250,
  );

  const [connectorTypeFiltersState, debouncedConnectorTypeFiltersState, setConnectorTypeFiltersState] = useDebounceState<
    ConnectorTypeLabel[]
  >([], 250);

  const [vehicleConnectorTypeFiltersState, setVehicleConnectorTypeFiltersState] = useState<ConnectorType[]>([]);

  const powerTypeFilters = powerTypeFiltersState.map((powerType: ConnectorPowerLabel) => {
    return { id: powerType, label: t(`missionControl.layers.filters.powerType.options.${powerType}`).toString() };
  });

  const updatePowerTypeFilter = (value: OnChangeValue<MSelectChangeData<null, ConnectorPowerLabel>, true>) => {
    setPowerTypeFiltersState(() => value.map((item: MSelectOption<null, ConnectorPowerLabel>) => item.id));
  };

  const updateOpertorFilter = (value: OnChangeValue<MSelectChangeData<null, string>, true>) => {
    setoOperatorFilterState(() => value.map((item: MSelectOption<null, string>) => item.id));
  };

  const updateConnectorStatusFilter = (connectorFilterStatus: ConnectorStatus) => {
    setConnectorFilterStatus((prev: ConnectorStatus | null) => (prev === connectorFilterStatus ? null : connectorFilterStatus));
  };

  const connectorTypeFilters =
    connectorTypeFiltersState.map((powerType: ConnectorTypeLabel) => {
      return { id: powerType, label: t(`missionControl.layers.filters.powerType.options.${powerType}`).toString() };
    }) || [];

  const operatorFilters =
    operatorFilterState.map((powerType: string) => {
      return { id: powerType, label: t(`missionControl.layers.filters.powerType.options.${powerType}`).toString() };
    }) || [];

  const updateConnectorTypeFilter = (value: OnChangeValue<MSelectChangeData<null, ConnectorTypeLabel>, true>) => {
    setConnectorTypeFiltersState(() => value.map((item: MSelectOption<null, ConnectorTypeLabel>) => item.id));
  };

  const updateVehicleConnectorTypeFilter = (connectorTypes: ConnectorType[]) => {
    setVehicleConnectorTypeFiltersState(() => connectorTypes);
  };

  useEffect(() => {
    const powerTypeFilterQuery: ConnectorPowerType[] = [];

    if (debouncedPowerTypeFiltersState.includes(ConnectorPowerLabel.Slow)) {
      powerTypeFilterQuery.push(ConnectorPowerType.AC_1_phase);
    }

    if (debouncedPowerTypeFiltersState.includes(ConnectorPowerLabel.Fast)) {
      powerTypeFilterQuery.push(ConnectorPowerType.AC_2_phase, ConnectorPowerType.AC_2_phase);
    }

    if (debouncedPowerTypeFiltersState.includes(ConnectorPowerLabel.Turbo)) {
      powerTypeFilterQuery.push(ConnectorPowerType.AC_3_phase, ConnectorPowerType.DC);
    }

    const connectorTypeFilter: ConnectorType[] = [];

    if (debouncedConnectorTypeFiltersState.includes(ConnectorTypeLabel.NEMA)) {
      connectorTypeFilter.push(
        ConnectorType.NEMA_10_30,
        ConnectorType.NEMA_10_50,
        ConnectorType.NEMA_14_30,
        ConnectorType.NEMA_14_50,
        ConnectorType.NEMA_5_20,
        ConnectorType.NEMA_6_30,
        ConnectorType.NEMA_6_50,
      );
    }

    if (debouncedConnectorTypeFiltersState.includes(ConnectorTypeLabel.EC_Type_1)) {
      connectorTypeFilter.push(ConnectorType.IEC_62196_T1, ConnectorType.IEC_62196_T1_COMBO);
    }

    if (debouncedConnectorTypeFiltersState.includes(ConnectorTypeLabel.TESLA)) {
      connectorTypeFilter.push(ConnectorType.TESLA);
    }

    if (debouncedConnectorTypeFiltersState.includes(ConnectorTypeLabel.CHADEMO)) {
      connectorTypeFilter.push(ConnectorType.CHADEMO);
    }

    if (debouncedConnectorTypeFiltersState.includes(ConnectorTypeLabel.GBT)) {
      connectorTypeFilter.push(ConnectorType.GBT);
    }

    if (debouncedConnectorTypeFiltersState.includes(ConnectorTypeLabel.CHAOJI)) {
      connectorTypeFilter.push(ConnectorType.CHAOJI);
    }

    if (debouncedConnectorTypeFiltersState.includes(ConnectorTypeLabel.IEC_Type_3)) {
      connectorTypeFilter.push(ConnectorType.IEC_62196_T3A, ConnectorType.IEC_62196_T3C);
    }

    if (debouncedConnectorTypeFiltersState.includes(ConnectorTypeLabel.IEC_Type_2_J1772)) {
      connectorTypeFilter.push(ConnectorType.IEC_62196_T2);
    }

    if (debouncedConnectorTypeFiltersState.includes(ConnectorTypeLabel.IEC_Type_2)) {
      connectorTypeFilter.push(ConnectorType.IEC_62196_T2_COMBO);
    }

    const connectorTypeFilterQuery = [...new Set([...connectorTypeFilter, ...vehicleConnectorTypeFiltersState])];

    setLoadingState(true);
    http.chargingPoint
      .get({
        search,
        connectorPowerType: powerTypeFilterQuery,
        connectorType: connectorTypeFilterQuery,
        operators: debouncedOperatorFilterState,
        ...(connectorFilterStatus ? { connectorStatus: connectorFilterStatus } : {}),
      })
      .then((response: IGetChargingPointResponse['records']) => {
        setChargingPointsState(() => response);
      })
      .catch((error) => {
        console.error(error);
      }).finally(() => {
        setLoadingState(false);
      });
  }, [
    debouncedPowerTypeFiltersState,
    debouncedConnectorTypeFiltersState,
    debouncedOperatorFilterState,
    vehicleConnectorTypeFiltersState,
    connectorFilterStatus,
    search,
  ]);

  return {
    loadingState,
    powerTypeFilters,
    connectorTypeFilters,
    operatorFilters,
    connectorFilter: connectorFilterStatus,
    updatePowerTypeFilter,
    updateOpertorFilter,
    updateConnectorTypeFilter,
    updateConnectorStatusFilter,
    updateVehicleConnectorTypeFilter,
    chargingPoints: chargingPointsState,
  };
};

export default MapLayersHook;
